import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useBranding } from "../lib/branding";
import SEO from "../components/SEO";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function AdminReset() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const { logoUrl } = useBranding();
    const nav = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) toast.error("Missing reset token. Use the link from your email.");
    }, [token]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) return toast.error("Password must be at least 8 characters.");
        if (password !== confirm) return toast.error("Passwords don't match.");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            toast.success("Password updated. Redirecting to login…");
            setTimeout(() => nav("/admin/login"), 1500);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reset failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-black text-white min-h-screen grid place-items-center px-4">
            <SEO title="Reset Password" noindex />
            <div className="w-full max-w-md">
                <Link to="/" className="flex justify-center mb-8" data-testid="reset-logo-link">
                    <img src={logoUrl} alt="MyStreetz" className="brand-logo h-10" />
                </Link>
                <form
                    onSubmit={onSubmit}
                    data-testid="admin-reset-form"
                    className="bg-streetz-paper border border-streetz-line p-8 flex flex-col gap-4"
                >
                    <h1 className="font-display uppercase text-3xl">Set a new password</h1>
                    <p className="text-streetz-muted text-sm">
                        Enter a new password (at least 8 characters) for your editor account.
                    </p>
                    {!token && (
                        <p className="overline text-streetz-red text-xs" data-testid="reset-missing-token">
                            This page needs a valid reset link from your email.
                        </p>
                    )}
                    <label className="flex flex-col gap-1">
                        <span className="overline">New password</span>
                        <input
                            type="password"
                            required
                            minLength={8}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="reset-new-password"
                            className="bg-black border border-streetz-line px-4 py-3 focus:border-streetz-red outline-none"
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="overline">Confirm password</span>
                        <input
                            type="password"
                            required
                            minLength={8}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            data-testid="reset-confirm-password"
                            className="bg-black border border-streetz-line px-4 py-3 focus:border-streetz-red outline-none"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={loading || !token || done}
                        data-testid="reset-submit"
                        className="bg-streetz-red text-white overline py-3 hover:bg-white hover:text-black disabled:opacity-60"
                    >
                        {done ? "PASSWORD UPDATED" : loading ? "UPDATING…" : "UPDATE PASSWORD"}
                    </button>
                    <Link
                        to="/admin/login"
                        data-testid="reset-back-to-login"
                        className="overline text-streetz-muted hover:text-streetz-yellow text-center text-xs"
                    >
                        ← Back to sign in
                    </Link>
                </form>
            </div>
        </div>
    );
}
